import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import "../styles/UserGuide.css";

const sections = [
  { id: "getting-started",    emoji: "🏠", label: "Getting Started"     },
  { id: "browsing-recipes",   emoji: "🔍", label: "Browsing Recipes"    },
  { id: "scaling",            emoji: "⚖️", label: "Recipe Scaling"      },
  { id: "meal-planner",       emoji: "📅", label: "Weekly Meal Planner" },
  { id: "printing",           emoji: "🖨️", label: "Printing Features"   },
  { id: "adding-editing",     emoji: "➕", label: "Adding & Editing"    },
  { id: "manage-ingredients", emoji: "🧅", label: "Manage Ingredients"  },
  { id: "tips",               emoji: "💡", label: "Tips & Tricks"       },
  { id: "data-storage",       emoji: "💾", label: "Data Storage"        },
];

function UserGuide() {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollToTop = () => {
    document.getElementById("guide-top")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.div
      animate={{ opacity: 1 }}
      initial={{ opacity: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="user-guide-container">

        <div id="guide-top" />

        <h1>User Guide</h1>
        <p className="intro">Welcome to Jason’s Delicious Recipes! This guide will walk you through everything the app has to offer so you can get the most out of it. If you have suggestions (use the subject line “Suggestion”) or run into any issues (use “Bug”), feel free to email me: j_hawthor@me.com.</p>

        {/* ── Table of Contents ── */}
        <nav className="guide-toc">
          <h2 className="toc-heading">Quick Navigation</h2>
          <ul className="toc-list">
            {sections.map(({ id, emoji, label }) => (
              <li key={id}>
                <button className="toc-link" onClick={() => scrollToSection(id)}>
                  <span className="toc-emoji">{emoji}</span>
                  {label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* ══════════════════════════════════════════════════════════════════
            GETTING STARTED
        ══════════════════════════════════════════════════════════════════ */}
        <section className="guide-section" id="getting-started">
          <h2>🏠 Getting Started</h2>
          <div className="guide-image-container">
            <img src="/images/guide/home-page.png" alt="Home page showing recipe cards" className="guide-image" />
            <p className="image-caption">Browse recipes on the home page</p>
          </div>
          <p>Browse through our collection of delicious recipes organized by category type. Click on any recipe card to view detailed instructions, ingredients, and equipment needed.</p>
          <ul>
            <li><strong>Home Page:</strong> Shows all recipes in your database.  Return to the home page at any time by clicking on "Delicious Recipes" logo displayed in the top left of any page.</li>
            <li><strong>Categories:</strong> Filter recipes by category (Main Course, Healthy, Side Dish, Sauce, Dessert, Bread, Breakfast, and Drink).</li>
            <div className="guide-image-container">
              <img src="/images/guide/categories.png" alt="Category buttons" className="guide-image" />
              <p className="image-caption">Filter recipes by category</p>
            </div>
            <li><strong>Search:</strong> Dish Type keywords like (american, mexican, chicken, low carb, snack, drink) to find specific recipes, or type an ingredient name to see all recipes that use it.</li>
            <div className="guide-image-container">
              <img src="/images/guide/search-bar.png" alt="Search bar" className="guide-image" />
              <p className="image-caption">Search for recipes by keyword</p>
            </div>
          </ul>
        </section>

        {/* ══════════════════════════════════════════════════════════════════
            BROWSING RECIPES
        ══════════════════════════════════════════════════════════════════ */}
        <section className="guide-section" id="browsing-recipes">
          <h2>🔍 Browsing Recipes</h2>
          <h3>Recipe Cards</h3>
          <p>Each recipe displays a beautiful image and title. Click on any recipe to view:</p>
          <ul>
            <li><strong>Instructions:</strong> Step-by-step cooking directions with optional images</li>
            <li><strong>Ingredients:</strong> Complete list of everything you need — with built-in scaling</li>
            <li><strong>Equipment:</strong> Tools and appliances required (with images if available)</li>
          </ul>
          <div className="guide-image-container">
            <img src="/images/guide/recipe-page.png" alt="Recipe detail page showing instructions, ingredients, and equipment tabs" className="guide-image" />
            <p className="image-caption">Recipe page with tabs for instructions, ingredients, and equipment</p>
          </div>

          <h3>Searching</h3>
          <div className="guide-image-container">
            <img src="/images/guide/search-bar.png" alt="Search bar" className="guide-image" />
            <p className="image-caption">Search for recipes by keyword</p>
          </div>
          <p>The search feature looks through recipe titles, ingredients, and dish types to find exactly what you're looking for.</p>
        </section>

        {/* ══════════════════════════════════════════════════════════════════
            RECIPE SCALING
        ══════════════════════════════════════════════════════════════════ */}
        <section className="guide-section" id="scaling">
          <h2>⚖️ Recipe Scaling</h2>
          <p>
            Need to cook for a crowd, or just making a smaller batch? The <strong>Ingredients</strong> tab has a
            built-in servings scaler that automatically adjusts every ingredient quantity for you.
          </p>
          <div className="guide-image-container">
            <img src="/images/guide/servings-scaler.png" alt="Servings scaler with minus, number, and plus buttons" className="guide-image" />
            <p className="image-caption">The servings scaler in the Ingredients tab</p>
          </div>
          <h3>How to use it:</h3>
          <ol>
            <li>Open any recipe and click the <strong>Ingredients</strong> tab</li>
            <li>Use the <strong>−</strong> and <strong>+</strong> buttons to decrease or increase servings, or type a number directly into the box</li>
            <li>All ingredient quantities update instantly — fractions are kept clean (e.g. <em>1/2 cup</em> becomes <em>1 cup</em>, not <em>0.5 cups</em>)</li>
            <li>Scaled ingredients are shown in <strong>bold</strong> with an orange <strong>*</strong> — hover over the asterisk to see the original amount</li>
            <li>A pill badge shows the current multiplier (e.g. <strong>× 2</strong> or <strong>÷ 2</strong>)</li>
            <li>Click <strong>Reset</strong> inside the badge to return to the original servings</li>
          </ol>
          <div className="info-box">
            <p><strong>💡 Smart scaling:</strong> The scaler handles whole numbers, fractions (1/2, 3/4), mixed numbers (1 1/2), ranges (6–7 cups), and "X and Y/Z" style quantities. Temperatures (350°) and sizes (8") are never changed.</p>
          </div>
          <p>
            The <strong>🛒 Print Grocery List</strong> button always prints the <em>scaled</em> quantities,
            so your shopping list matches exactly what you're planning to make.
          </p>
        </section>

        {/* ══════════════════════════════════════════════════════════════════
            WEEKLY MEAL PLANNER
        ══════════════════════════════════════════════════════════════════ */}
        <section className="guide-section" id="meal-planner">
          <h2>📅 Weekly Meal Planner</h2>
          <div className="guide-image-container">
            <img src="/images/guide/meal-planner.png" alt="Weekly meal planner showing 7 days" className="guide-image" />
            <p className="image-caption">Your weekly meal planner — scroll horizontally to see all seven days</p>
          </div>
          <p>Plan your weekly dinners in advance! The meal planner appears at the top of every page and saves your selections automatically.</p>

          <h3>Adding Recipes to Your Meal Plan</h3>
          <div className="highlight-box">
            <strong>Right-click on any recipe image</strong> to add it to your weekly meal plan as a main dish or a side!
          </div>
          <div className="guide-image-container">
            <img src="/images/guide/context-menu-two-column.png" alt="Two-column context menu showing Main and Side buttons for each day" className="guide-image" />
            <p className="image-caption">Right-click menu — choose Main or Side for each day of the week</p>
          </div>
          <p>The right-click menu shows a row for each day of the week with two buttons:</p>
          <ul>
            <li>
              <strong>Main button (blue):</strong> Sets this recipe as the main meal for that day.
              <ul>
                <li><em>main</em> — day is empty, click to assign</li>
                <li><em>replace</em> — day already has a meal, click to swap it out</li>
                <li><em>✓ main</em> — this recipe is already the main meal for that day</li>
              </ul>
            </li>
            <li>
              <strong>Side button (green):</strong> Adds this recipe as a side dish alongside the main meal.
              <ul>
                <li><em>side</em> — click to add as a side</li>
                <li><em>✓ side</em> — already added as a side (button disabled to prevent duplicates)</li>
              </ul>
            </li>
          </ul>
          <div className="info-box">
            <p><strong>💡 Note:</strong> Special meal days (Dine Out, Delivery, Date Night) only show the Main button since sides don't apply.</p>
          </div>

          <h3>Adding from the Recipe Page</h3>
          <div className="guide-image-container">
            <img src="/images/guide/add-to-plan-button.png" alt="Green Add to Plan button on recipe detail page" className="guide-image" />
            <p className="image-caption">📅 Add to Plan button on every recipe page</p>
          </div>
          <p>
            You can also add a recipe to your plan directly from its recipe page. Click the green <strong>📅 Add to Plan</strong> button
            (next to the Print buttons) and the same two-column day selector will appear, letting you assign it as a main or side for any day.
          </p>

          <h3>Side Dishes</h3>
          <div className="guide-image-container">
            <img src="/images/guide/sides-section.png" alt="Day card showing collapsible Sides section with added sides" className="guide-image" />
            <p className="image-caption">Each meal card has a collapsible Sides section</p>
          </div>
          <p>Every day card that has a main meal includes a <strong>Sides</strong> section below the recipe image:</p>
          <ul>
            <li>Click <strong>▸ Sides</strong> to expand and see the sides list, or collapse it to keep things tidy</li>
            <li>The header shows a count in parentheses — e.g. <strong>▾ Sides (2)</strong> — so you can see at a glance how many sides are planned</li>
            <li>Click the green <strong>＋</strong> button next to the Sides header to search for and add a side dish from any recipe in your collection</li>
            <li>Each side title is a clickable link that takes you directly to that recipe's page</li>
            <li>Click <strong>×</strong> on any side to remove it from that day</li>
            <li>Sides are saved with your meal plan — they're included when you export or import</li>
          </ul>

          <h3>Drag to Reorder Days</h3>
          <div className="guide-image-container">
            <img src="/images/guide/drag-to-reorder.png" alt="Meal card being dragged showing orange border, with blue border on target day" className="guide-image" />
            <p className="image-caption">Drag the ⠿ handle to swap meals between days</p>
          </div>
          <p>Changed your mind about which night to cook what? You can drag and drop meals to swap them between days:</p>
          <ol>
            <li>Look for the <strong>⠿</strong> drag handle in the top-left corner of any meal card that has a recipe assigned</li>
            <li>Click and drag the handle to another day's card</li>
            <li>
              Visual cues tell you what will happen:
              <ul>
                <li><strong>Orange border</strong> — the card you're currently dragging</li>
                <li><strong>Green border</strong> — dragging over an empty day (the meal will move there)</li>
                <li><strong>Blue border</strong> — dragging over an occupied day (the two meals will swap)</li>
              </ul>
            </li>
            <li>Release to complete the swap — the full meal including all sides moves together</li>
          </ol>
          <div className="info-box">
            <p><strong>💡 Note:</strong> Empty days show no drag handle since there's nothing to move. Dragging onto the same card you picked up does nothing.</p>
          </div>

          <h3>Scaling Meals in Your Plan</h3>
          <div className="guide-image-container">
            <img src="/images/guide/plan-scale-badge.png" alt="Meal card showing 👥 8 srv × 2 badge with edit pencil" className="guide-image" />
            <p className="image-caption">Every meal card shows a servings badge — click to adjust</p>
          </div>
          <p>
            Every meal card shows a <strong>👥 N srv</strong> badge indicating how many servings are planned for that day.
            When a recipe is scaled, the badge turns blue and shows the multiplier (e.g. <strong>👥 8 srv × 2</strong>).
            The grocery list uses this scale to adjust ingredient quantities automatically.
          </p>
          <h4>How scaling gets set:</h4>
          <ul>
            <li>
              <strong>From the Recipe page:</strong> If you've used the servings scaler before clicking <strong>📅 Add to Plan</strong>,
              the current servings count is captured and saved with the meal.
            </li>
            <li>
              <strong>From browse pages (right-click):</strong> Recipes added by right-clicking always start at <em>1× (base servings)</em>.
            </li>
          </ul>
          <h4>Editing the scale on a card:</h4>
          <ol>
            <li>Click the <strong>👥 N srv</strong> badge on any meal card</li>
            <li>An inline input appears — type the number of servings you want to make</li>
            <li>Press <strong>Enter</strong> or click <strong>✓</strong> to save — the multiplier is calculated automatically</li>
            <li>Press <strong>Escape</strong> or click <strong>✕</strong> to cancel without changes</li>
          </ol>
          <div className="guide-image-container">
            <img src="/images/guide/plan-scale-editor.png" alt="Inline servings editor open on a meal card" className="guide-image" />
            <p className="image-caption">Inline servings editor — type target servings and press ✓</p>
          </div>
          <div className="info-box">
            <p><strong>💡 Note:</strong> Scaling only affects the <em>main meal</em> for that day. Side dishes always use their base servings in the grocery list.</p>
          </div>

          <h3>Special Meal Options</h3>
          <div className="guide-image-container">
            <img src="/images/guide/special-meals.png" alt="Special meal cards for Dine Out, Delivery, and Date Night" className="guide-image" />
            <p className="image-caption">Special meal options on the home page</p>
          </div>
          <p>On the home page, you'll find special meal cards for Dine Out, Delivery, and Date Night. Right-click these cards just like regular recipes to add them to your week.</p>

          <h3>Managing Your Meal Plan</h3>
          <div className="guide-image-container">
            <img src="/images/guide/meal-planner-buttons.png" alt="Meal planner control buttons" className="guide-image" />
            <p className="image-caption">Meal planner control buttons</p>
          </div>
          <ul>
            <li><strong>Show/Hide:</strong> Toggle the planner visibility to free up screen space</li>
            <li><strong>Remove:</strong> Click "Remove" on any meal card to clear that day's main meal — a confirmation dialog shows the recipe name before removing (sides are also cleared)</li>
            <li><strong>Clear All:</strong> Remove all meals at once (with confirmation)</li>
          </ul>
          <strong>Note:</strong> When the grocery list appears, you can click the <strong>checkbox</strong> to mark items you already have on hand.

          <h3>Grocery List</h3>
          <div className="guide-image-container">
            <img src="/images/guide/grocery-list-modal.png" alt="Grocery list modal showing By Recipe and Combined tabs" className="guide-image" />
            <p className="image-caption">Grocery list with By Recipe and Combined views</p>
          </div>
          <p>
            Click the <strong>🛒 Grocery List</strong> button to open a combined shopping list for your entire week.
            The list includes ingredients from both main meals and side dishes.
          </p>
          <ul>
            <li>
              <strong>By Recipe view:</strong> Ingredients grouped by recipe. Side dishes appear indented below their
              main meal with a green <em>SIDE</em> badge and a dashed border. If a main meal is scaled,
              its ingredient quantities are adjusted accordingly.
            </li>
            <li>
              <strong>Combined List view:</strong> All ingredients from mains and sides pooled together —
              scaled quantities are applied before combining, so if Tuesday's chicken recipe is at × 2,
              its ingredients count double. Duplicates are summed and grouped by <strong>store aisle</strong>
              (Produce → Meat → Poultry → Seafood → Dairy → etc.) for easy in-order shopping.
              Click any category header to collapse or expand that section.
              Items combined across recipes are flagged with a <em>combined</em> badge.
            </li>
            <li><strong>Checkboxes:</strong> Tap or click any ingredient to check it off as you shop</li>
            <li><strong>Print:</strong> Print the grocery list directly from the modal — scaled quantities and side dishes both carry through</li>
          </ul>
          <div className="guide-image-container">
            <img src="/images/guide/grocery-list-sides.png" alt="Grocery list showing side dishes indented below main meals with SIDE badge" className="guide-image" />
            <p className="image-caption">Side dishes appear indented with a SIDE badge in the By Recipe view</p>
          </div>

          {/* ── Non-Food Items ── */}
          <h3>🧴 Non-Food Items</h3>
          <p>
            The grocery list modal includes a built-in <strong>Non-Food Items</strong> panel so you can add
            household, personal care, cleaning supplies, baby products, and other store staples to the same
            trip — giving you one complete list for the entire store run.
          </p>
          <div className="guide-image-container">
            <img src="/images/guide/non-food-panel.png" alt="Non-Food Items collapsible panel at the bottom of the grocery list modal" className="guide-image" />
            <p className="image-caption">The Non-Food Items panel at the bottom of the grocery list modal</p>
          </div>

          <h4>How to use it:</h4>
          <ol>
            <li>Open the <strong>🛒 Grocery List</strong> modal</li>
            <li>Scroll to the bottom and click the <strong>🧴 Non-Food Items</strong> bar to expand the panel</li>
            <li>Click any category header to expand it (Paper &amp; Plastic Goods, Cleaning &amp; Household, Personal Care, etc.)</li>
            <li>Check off any items you need to pick up — items get a strikethrough when checked</li>
            <li>A counter badge on the panel header shows how many items are selected at a glance</li>
            <li>Click <strong>🖨️ Print Grocery List</strong> — your checked non-food items are automatically appended to the printout after a bold divider</li>
          </ol>

          <div className="info-box">
            <p>
              <strong>💾 Selections persist for your session.</strong> Your checked non-food items are remembered
              while you have the app open. When you're back from the store, click <strong>Clear All</strong> inside
              the panel header to reset for next week.
            </p>
          </div>

          <h4>Non-Food Categories:</h4>
          <ul>
            <li>🪣 <strong>Paper &amp; Plastic Goods</strong> — plastic wrap, tin foil, wax paper, parchment paper, trash bags, paper plates, plastic cups, plastic utensils, straws, and more</li>
            <li>🧹 <strong>Cleaning &amp; Household</strong> — dish soap, dishwasher detergent, laundry detergent, fabric softener, general purpose cleaner, window cleaner, bleach, dryer sheets, air freshener, batteries, light bulbs, and more</li>
            <li>🧴 <strong>Personal Care</strong> — hand soap, shampoo, conditioner, body wash, deodorant, toothpaste, toothbrushes, feminine products, razors, lotion, sunscreen, vitamins/supplements, pain reliever, allergy medicine, and more</li>
            <li>👶 <strong>Baby &amp; Infant</strong> — diapers (all sizes), baby wipes, baby food (stages 1–3), baby formula, baby shampoo, baby lotion, diaper rash cream, pacifiers, and more</li>
            <li>🌿 <strong>Outdoor &amp; Seasonal</strong> — bug spray, charcoal, lighter fluid, grill brushes, sunscreen, citronella candles, ice melt, and more</li>
            <li>🐾 <strong>Pet Supplies</strong> — dog food, cat food, cat litter, pet treats, pet shampoo, flea &amp; tick treatment, and more</li>
          </ul>

          <h4>How it prints:</h4>
          <p>
            When you print the grocery list, checked non-food items appear as a clearly separated section at the
            bottom of the printout — organized by category with checkboxes, just like the food items.
            If no non-food items are checked, the section is omitted entirely so the print stays clean.
          </p>
          <div className="guide-image-container">
            <img src="/images/guide/non-food-print.png" alt="Printed grocery list showing Non-Food Items section below a bold divider" className="guide-image" />
            <p className="image-caption">Non-food items print after a divider at the bottom of your grocery list</p>
          </div>

          <h3>Export &amp; Import</h3>
          <p><strong>Export:</strong> Save your meal plan (including all sides) as a JSON file. You'll be prompted to name it (e.g., "march-meal-plan").</p>
          <p><strong>Import:</strong> Load a previously saved meal plan from a JSON file.</p>
          <div className="guide-image-container">
            <img src="/images/guide/export-import.png" alt="Export and Import buttons with file dialog" className="guide-image" />
            <p className="image-caption">Export and import your meal plans</p>
          </div>
          <p><em>Use cases: Create seasonal meal plans, share plans with family, or keep weekly templates!</em></p>

          <h3>Print Your Meal Plan</h3>
          <p>Click the <strong>🖨️ Print</strong> button to print your weekly meal plan with images:</p>
          <ol>
            <li>Click the Print button</li>
            <li>Enter a week start date (e.g., "2026-02-23")</li>
            <li>Preview and print — side dishes appear listed below each main meal</li>
          </ol>
          <div className="guide-image-container">
            <img src="/images/guide/print-meal-plan.png" alt="Printed meal plan preview" className="guide-image" />
            <p className="image-caption">Print preview of your weekly meal plan</p>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════════════════
            PRINTING FEATURES
        ══════════════════════════════════════════════════════════════════ */}
        <section className="guide-section" id="printing">
          <h2>🖨️ Printing Features</h2>

          <h3>Print Recipe Instructions</h3>
          <div className="guide-image-container">
            <img src="/images/guide/print-buttons.png" alt="Print Instructions, Print Grocery List, and Add to Plan buttons" className="guide-image" />
            <p className="image-caption">Action buttons on each recipe page</p>
          </div>
          <p>From any recipe page, click <strong>🖨️ Print Instructions</strong> to get a formatted printout including:</p>
          <ul>
            <li>Recipe title and basic info (cuisine, prep time, servings)</li>
            <li>Equipment needed (with images)</li>
            <li>Step-by-step instructions (with images) — sections print with labeled headings if the recipe uses them</li>
          </ul>
          <div className="guide-image-container">
            <img src="/images/guide/print-instructions.png" alt="Printed instructions preview" className="guide-image" />
            <p className="image-caption">Print preview of recipe instructions</p>
          </div>

          <h3>Print Grocery List (Single Recipe)</h3>
          <p>Click <strong>🛒 Print Grocery List</strong> on any recipe page to get a shopping-friendly list with:</p>
          <ul>
            <li>All ingredients with checkboxes — grouped by section if the recipe uses them</li>
            <li>Quantities automatically reflect any scaling you've applied in the Ingredients tab</li>
            <li>Servings information (with a note if scaled)</li>
            <li>Clean, minimal format perfect for the store</li>
          </ul>
          <div className="guide-image-container">
            <img src="/images/guide/print-grocery-list.png" alt="Printed grocery list with checkboxes" className="guide-image" />
            <p className="image-caption">Print preview of grocery list</p>
          </div>

          <h3>Print Weekly Grocery List (with Non-Food Items)</h3>
          <p>
            The <strong>🖨️ Print Grocery List</strong> button inside the weekly grocery list modal produces a
            complete store list combining food ingredients and any checked non-food items:
          </p>
          <ul>
            <li><strong>Food items</strong> — printed by recipe (By Recipe view) or grouped by store aisle (Combined view), depending on which tab is active when you print</li>
            <li><strong>Non-food items</strong> — appended after a bold divider, organized by category with checkboxes</li>
            <li>If no non-food items are checked, the non-food section is omitted automatically — keeping the print clean</li>
          </ul>
          <div className="info-box">
            <p>
              <strong>💡 Tip:</strong> Switch to the <strong>Combined List</strong> tab before printing for the
              most store-friendly printout — food ingredients are sorted by aisle, and non-food items follow
              in their own categories right after.
            </p>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════════════════
            ADDING & EDITING RECIPES
        ══════════════════════════════════════════════════════════════════ */}
        <section className="guide-section" id="adding-editing">
          <h2>➕ Adding &amp; Editing Recipes</h2>
          <div className="guide-image-container">
            <img src="/images/guide/add-recipe-button.png" alt="Add Recipe button in navigation" className="guide-image" />
            <p className="image-caption">Add/Edit Recipe button in navigation</p>
          </div>
          <p>Create your own recipes or modify existing ones!</p>

          <h3>Creating a New Recipe</h3>
          <ol>
            <li>Click the <strong>+ ✏️</strong> button in the navigation</li>
            <li>The <strong>Recipe ID</strong> is automatically filled in with the next available number — you can change it if needed, but it must be unique</li>
            <li>Fill in all the recipe details:
              <ul>
                <li>Basic Info (ID, title, image, time, servings)</li>
                <li>Cuisines and dish types</li>
                <li>Occasions (Christmas, Birthday, etc.)</li>
                <li>Ingredients — using the structured entry form (see below)</li>
                <li>Instructions — step-by-step, or use sections (see below)</li>
                <li>Equipment (with optional images)</li>
              </ul>
            </li>
            <li>Click <strong>Save to Firebase</strong> to save the recipe directly to your account</li>
            <li>Optionally click <strong>Generate JSON</strong> to get a local backup copy</li>
          </ol>
          <div className="guide-image-container">
            <img src="/images/guide/add-recipe-form.png" alt="Add recipe form with all fields" className="guide-image" />
            <p className="image-caption">Add recipe form</p>
          </div>

          <h3>Adding a Recipe Image</h3>
          <p>
            Recipe images are uploaded directly to <strong>Firebase Storage</strong> — no file paths or manual copying required.
            Images are stored securely in your account and load instantly from the cloud. 
          </p>
          <ul>
            <li>The recommended <strong>recipe image</strong> size is:<strong>Height: 370 Width: 550</strong></li>
            <li>The recommended <strong>recipe instruction image</strong> size is:<strong>Height: 370 Width: 550</strong></li>
            <li>The recommended <strong>equipment image</strong> size is:<strong>Height: 165 Width: 300</strong></li>
          </ul>
          <ol>
            <li>In the <strong>Basic Information</strong> section, click <strong>Upload Image</strong></li>
            <li>Select an image file from your device (JPG, PNG, WebP, etc.)</li>
            <li>The image uploads automatically and a thumbnail preview appears</li>
            <li>Click <strong>Replace Image</strong> at any time to swap it out</li>
            <li>Click <strong>Remove Image</strong> to clear it</li>
          </ol>
          <div className="info-box">
            <p>
              <strong>💡 Images for steps and equipment</strong> work the same way — click <strong>Upload Image</strong>
              inside any step or equipment card, then add a description, and click <strong>Add Image</strong> to attach it.
              A thumbnail preview confirms the upload before you commit.
            </p>
          </div>

          <h3>Entering Ingredients</h3>
          <p>
            Ingredients are entered using a structured form rather than a single free-text field.
            This makes grocery list combining much more accurate — the app knows that
            "scallions" and "green onion" are the same thing, and can sum quantities correctly.
          </p>
          <div className="guide-image-container">
            <img src="/images/guide/ingredient-entry-form.png" alt="Structured ingredient entry form showing Qty, Unit, Ingredient name, and Prep fields" className="guide-image" />
            <p className="image-caption">Structured ingredient entry form</p>
          </div>
          <p>Each ingredient is entered using four fields:</p>
          <ul>
            <li><strong>Qty:</strong> The quantity as a decimal number (e.g. <em>1.5</em> for one and a half). Supports 0.25 increments.</li>
            <li>
              <strong>Unit:</strong> A dropdown grouped into four categories:
              <ul>
                <li><em>Volume</em> — tsp, tbsp, fl oz, cup, pt, qt, gal, ml, l</li>
                <li><em>Weight</em> — oz, lb, g, kg</li>
                <li><em>Count</em> — whole, slice, clove, sprig, stalk, can, pkg</li>
                <li><em>Special</em> — pinch, dash, to taste, as needed</li>
              </ul>
              Leave blank if the ingredient has no unit (e.g. "3 eggs").
            </li>
            <li>
              <strong>Ingredient name:</strong> Start typing and a dropdown will suggest matching ingredients
              from the standard list — showing the ingredient name and its category.
              If your ingredient isn't in the list, two options appear at the bottom of the dropdown:
              <ul>
                <li><em>"+ Use as custom ingredient"</em> — adds it to the recipe as-is and tracks it in the yellow panel below for saving later</li>
                <li><em>"+ Add to ingredient list"</em> — expands an inline category picker so you can assign a store-aisle category and save it to your ingredient list immediately. Once saved, it appears in autocomplete right away and is recognized by the grocery combining logic</li>
              </ul>
            </li>
            <li><strong>Prep (optional):</strong> How the ingredient is prepared — e.g. <em>diced</em>, <em>sifted</em>, <em>melted</em>, <em>finely chopped</em>.</li>
          </ul>
          <div className="guide-image-container">
            <img src="/images/guide/ingredient-autocomplete.png" alt="Ingredient name autocomplete dropdown showing matching suggestions with category labels" className="guide-image" />
            <p className="image-caption">Autocomplete suggests ingredients from the standard list</p>
          </div>

          <h3>Custom Ingredients &amp; Adding to the Master List</h3>
          <p>
            If an ingredient isn't in the autocomplete list, select <strong>+ Use "…" as custom ingredient</strong>.
            The recipe will save and display correctly — but the ingredient won't be recognized by the
            grocery list combining logic until it's added to the master list.
          </p>
          <p>
            The app tracks every custom ingredient used in the recipe and shows a panel at the bottom of the page:
          </p>
          <div className="info-box">
            <p>
              <strong>🆕 New Ingredients to Add</strong> — a yellow card appears for each custom ingredient showing:
            </p>
            <ul>
              <li>A <strong>Category dropdown</strong> — assign the correct store-aisle category (defaults to <em>Produce</em>)</li>
              <li>An <strong>Aliases field</strong> — optionally add alternate names (comma-separated), e.g. <em>scallions, spring onions</em></li>
              <li>A <strong>Save to Firebase</strong> button — saves that ingredient to your Firebase database immediately</li>
              <li>A <strong>✕ button</strong> to dismiss any ingredient you don't want to save (e.g. a one-off item)</li>
            </ul>
            <p>
              Once saved, the ingredient appears immediately in autocomplete and is recognized by the grocery combining logic.
              Use <strong>Save All to Firebase</strong> at the bottom of the panel to save all pending ingredients at once.
            </p>
          </div>
          <div className="guide-image-container">
            <img src="/images/guide/custom-ingredient-panel.png" alt="Custom ingredients panel showing yellow cards with category and alias fields" className="guide-image" />
            <p className="image-caption">Custom ingredient panel appears after Generate JSON when new ingredients are detected</p>
          </div>

          <h4>Original string (auto-generated)</h4>
          <p>
            As you fill in the fields, the <strong>Original string</strong> field at the bottom of the form is
            built automatically — for example, entering Qty <em>1.5</em>, Unit <em>cup</em>, Name <em>all-purpose flour</em>,
            Prep <em>sifted</em> produces: <code>1 1/2 cups all-purpose flour sifted</code>.
          </p>
          <p>
            This is the string that displays on the recipe page. If you need to write it differently, check the
            <strong> Override</strong> checkbox to unlock the field and edit it directly.
          </p>
          <div className="guide-image-container">
            <img src="/images/guide/ingredient-original-string.png" alt="Original string field showing auto-generated text with Override checkbox" className="guide-image" />
            <p className="image-caption">Original string is auto-generated but can be overridden</p>
          </div>

          <h3>Editing an Existing Ingredient</h3>
          <div className="guide-image-container">
            <img src="/images/guide/ingredient-edit-highlight.png" alt="Ingredient row highlighted in yellow with editing label and Cancel Edit button" className="guide-image" />
            <p className="image-caption">Ingredient highlighted in yellow while being edited</p>
          </div>
          <p>To update an ingredient you've already added:</p>
          <ol>
            <li>Click the <strong>Edit</strong> button on the ingredient row</li>
            <li>The ingredient is highlighted in yellow with an <em>✏️ editing…</em> label</li>
            <li>The structured form opens below, pre-filled with the ingredient's current values</li>
            <li>Make your changes and click <strong>Update Ingredient</strong></li>
            <li>Click <strong>Cancel Edit</strong> to abandon without changes — the ingredient is restored to its original position</li>
          </ol>
          <div className="info-box">
            <p><strong>💡 Note:</strong> The Edit and Remove buttons on an ingredient are disabled while that ingredient is being edited, to prevent conflicting changes.</p>
          </div>

          <h3>Recipes with Sections (Batter, Topping, Sauce, etc.)</h3>
          <p>
            Some recipes have distinct parts — like a crepe batter and a separate whipped cream filling. You can split
            both <strong>Ingredients</strong> and <strong>Instructions</strong> into named sections so they display
            clearly on the recipe page.
          </p>
          <h4>Adding Ingredient Sections:</h4>
          <ol>
            <li>Type a section name (e.g., <em>Batter</em>, <em>Topping</em>, <em>Sauce</em>) in the section field and click <strong>Add Section</strong></li>
            <li>Click <strong>Add Ingredients</strong> on the section to open the entry form</li>
            <li>Use the structured form to add each ingredient</li>
            <li>Repeat for additional sections (e.g., <em>Filling</em>, <em>Glaze</em>)</li>
          </ol>

          <h3>Adding &amp; Inserting Instruction Steps</h3>
          <div className="guide-image-container">
            <img src="/images/guide/insert-before-step.png" alt="Step row showing Insert Before, Edit, and Remove buttons" className="guide-image" />
            <p className="image-caption">Each step has Insert Before, Edit, and Remove buttons</p>
          </div>
          <p>Each instruction step has three action buttons:</p>
          <ul>
            <li><strong>Insert Before:</strong> Opens the step form configured to add a <em>new</em> step immediately before this one.</li>
            <li><strong>Edit:</strong> Pulls the step's text into the form for editing. Click <strong>Cancel</strong> to restore the original step unchanged.</li>
            <li><strong>Remove:</strong> Deletes the step and renumbers the remaining steps.</li>
          </ul>
          <div className="info-box">
            <p><strong>💡 Tip:</strong> Forgot a step? Use <strong>Insert Before</strong> on the step that should come <em>after</em> your missing one.</p>
          </div>

          <h3>Editing an Existing Recipe</h3>
          <ol>
            <li>Click <strong>+ ✏️</strong> in the navigation</li>
            <li>Click <strong>Load Recipe</strong> and use the search box to find it quickly</li>
            <li>Make your changes — ingredients and steps maintain their position when edited</li>
            <li>Click <strong>Save to Firebase</strong> to save your changes</li>
          </ol>
          <div className="guide-image-container">
            <img src="/images/guide/load-recipe-modal.png" alt="Load recipe modal with search box and recipe list" className="guide-image" />
            <p className="image-caption">Load recipe modal — search to find recipes quickly</p>
          </div>

          <h3>Sharing Recipes with Other Users</h3>
          <p>
            You can share any recipe with another user using the <strong>Generate JSON</strong> and <strong>Import JSON</strong> buttons.
          </p>
          <h4>To share a recipe:</h4>
          <ol>
            <li>Open the recipe in the Add/Edit Recipe page (click <strong>Load Recipe</strong> to find it)</li>
            <li>Click <strong>Generate JSON</strong> at the bottom of the page</li>
            <li>Click <strong>Download JSON</strong> to save the file to your device</li>
            <li>Send the <code>.json</code> file to the other user (email, message, etc.)</li>
          </ol>
          <h4>To import a shared recipe:</h4>
          <ol>
            <li>Click the <strong>Import JSON</strong> button in the top-right of the Add/Edit Recipe page</li>
            <li>Select the <code>.json</code> file you received</li>
            <li>The recipe loads into the form with a <strong>new ID automatically assigned</strong> — no collision with your existing recipes</li>
            <li>Review the recipe, make any changes, then click <strong>Save to Firebase</strong></li>
          </ol>
          <div className="info-box">
            <p>
              <strong>💡 Note:</strong> Images in a shared recipe are stored in the original owner's Firebase Storage and will still display correctly.
              If you want your own copy of the images, you can replace them using the Upload Image button before saving.
            </p>
          </div>

          <h3>Important Notes</h3>
          <div className="warning-box">
            <p><strong>⚠️ Navigation Warning:</strong> If you try to leave the Add/Edit Recipe page, you'll be warned to prevent accidental data loss.</p>
          </div>
          <ul>
            <li>Recipe IDs are auto-filled with the next available number — change only if you need a specific ID, and ensure it stays unique</li>
            <li>Images are uploaded directly to Firebase Storage — no file management or paths needed</li>
            <li>Use the <strong>Enter key</strong> as a shortcut when adding cuisines, dish types, occasions, and section names</li>
            <li>The <strong>Servings</strong> field becomes the base for scaling — make sure it's accurate</li>
            <li>Existing recipes that use plain text ingredients continue to display and scale correctly</li>
          </ul>
        </section>

        {/* ══════════════════════════════════════════════════════════════════
            MANAGE INGREDIENTS
        ══════════════════════════════════════════════════════════════════ */}
        <section className="guide-section" id="manage-ingredients">
          <h2>🧅 Manage Ingredients</h2>
          <p>
            The <strong>Manage Ingredients</strong> page (accessible from the navigation bar) lets you add,
            edit, delete, and search the master ingredient list that powers autocomplete and grocery list combining.
          </p>

          <h3>Adding a New Ingredient</h3>
          <ol>
            <li>Enter the <strong>canonical name</strong> — this is the name that appears in autocomplete and on grocery lists (e.g. <em>green onion</em>)</li>
            <li>Choose a <strong>Category</strong> — this controls where it appears in store-aisle grouped grocery lists</li>
            <li>Optionally add <strong>Aliases</strong> — type an alternate name and press Enter or click Add (e.g. <em>scallions</em>, <em>spring onions</em>). Any of these names typed in a recipe will resolve to this ingredient when combining the grocery list.</li>
            <li>Click <strong>Add Ingredient</strong></li>
          </ol>

          <h3>Editing or Deleting</h3>
          <ul>
            <li>Use the search box and category filter to find an ingredient quickly</li>
            <li>Click <strong>Edit</strong> on any ingredient row — an inline edit form opens directly below that row, highlighted in yellow. The name is locked; you can update the category and aliases</li>
            <li>Click <strong>Update</strong> to save, or <strong>Cancel</strong> to dismiss without changes</li>
            <li>Click <strong>Delete</strong> to remove it — a confirmation step appears inline before the deletion is committed</li>
          </ul>

          <h3>Store-Aisle Categories</h3>
          <p>Ingredients are organized into 12 grocery store-aisle categories. This controls the grouping order in the Combined grocery list:</p>
          <ul>
            <li>🥦 Produce</li>
            <li>🥩 Meat &amp; Pork</li>
            <li>🍗 Poultry</li>
            <li>🐟 Seafood</li>
            <li>🥛 Dairy &amp; Eggs</li>
            <li>🍞 Bread &amp; Bakery</li>
            <li>🌾 Grains, Pasta &amp; Rice</li>
            <li>🥫 Canned &amp; Jarred</li>
            <li>🧴 Oils, Sauces &amp; Condiments</li>
            <li>🌿 Herbs &amp; Spices</li>
            <li>🧁 Baking &amp; Sweets</li>
            <li>🛒 Other</li>
          </ul>

          <h3>Saving Your Changes</h3>
          <h3>Collapse All / Expand All</h3>
          <p>
            The <strong>▸ Collapse All</strong> button next to the filters collapses every category at once — useful when you have many categories open and want to quickly scan the list. It toggles to <strong>▾ Expand All</strong> when all categories are already collapsed.
          </p>

          <h3>Duplicate Prevention</h3>
          <p>
            If you type a name in the Add New Ingredient form that already exists in the list, an orange warning banner appears below the name field showing the existing ingredient's category and aliases. The <strong>Add Ingredient</strong> button is also disabled until you change the name — preventing accidental duplicates. Use <strong>Edit</strong> on the existing row to update it instead.
          </p>

          <div className="info-box">
            <p>
              <strong>Changes save instantly.</strong> Every add, edit, or delete on the Manage Ingredients page
              writes directly to Firebase — there's no Save button to click and no JSON file to update.
              Changes are available immediately in autocomplete across all devices.
            </p>
          </div>
          <p>Use the <strong>Export JSON</strong> button at the bottom of the page if you want a local backup copy of your full ingredient list.</p>
        </section>

        {/* ══════════════════════════════════════════════════════════════════
            TIPS & TRICKS
        ══════════════════════════════════════════════════════════════════ */}
        <section className="guide-section" id="tips">
          <h2>💡 Tips &amp; Tricks</h2>
          <ul>
            <li><strong>Keyboard shortcuts:</strong> Press Enter to quickly add cuisines, dish types, occasions, and section names</li>
            <li><strong>Meal planning:</strong> Use Export to create different meal plans for different weeks or seasons, then Import to load them back</li>
            <li><strong>Grocery shopping:</strong> Scale a recipe first, then print the grocery list — it always prints the scaled amounts</li>
            <li><strong>Scaling for crowds:</strong> Hosting a party? Scale up to 12, 24, or any number of servings — quantities convert to clean fractions automatically</li>
            <li><strong>Scaling down:</strong> Only cooking for one? Scale below the base servings and the math works just the same</li>
            <li><strong>Side dishes:</strong> Add sides to your plan using the right-click menu, the 📅 Add to Plan button on any recipe page, or the ＋ button inside a day card's Sides section</li>
            <li><strong>Planner scaling:</strong> Scale a recipe on its page <em>before</em> clicking 📅 Add to Plan — the servings count is captured automatically. Or just click the 👥 badge on any meal card later to adjust it directly</li>
            <li><strong>Grocery list accuracy:</strong> Scaled meals show their adjusted quantities in both the By Recipe and Combined grocery list views — so a doubled recipe contributes twice the ingredients to your shopping list</li>
            <li><strong>Grocery categories:</strong> The Combined grocery list groups items by store aisle — Produce first, then Meat, Poultry, Seafood, Dairy, and so on — so you can shop in order</li>
            <li><strong>Non-food items:</strong> Check off household, personal care, cleaning supplies, and other store staples in the Non-Food Items panel at the bottom of the grocery list modal — they print on the same list as your food items</li>
            <li><strong>Non-food items:</strong> Checked non-food items are remembered for the current session — click Clear All in the panel when you're done shopping to reset</li>
            <li><strong>Complete store list:</strong> Switch to the Combined List tab before printing for the most organized printout — food grouped by aisle, non-food items by category, all on one page</li>
            <li><strong>Rearranging your week:</strong> Drag meal cards using the ⠿ handle to swap days without losing sides or other meal data</li>
            <li><strong>Missed a step:</strong> Use Insert Before on any instruction step to slot in a forgotten step without rewriting the whole section</li>
            <li><strong>Recipe images:</strong> Images upload directly to Firebase Storage — just click Upload Image, pick a file, and you're done. No file paths or copying needed</li>
            <li><strong>Step &amp; equipment images:</strong> Upload images for individual instruction steps and equipment the same way — upload, add a description, then click Add Image</li>
            <li><strong>Auto recipe ID:</strong> The Recipe ID field fills in automatically when you start a new recipe — just leave it as-is unless you need a specific number</li>
            <li><strong>Custom ingredients:</strong> When you use a custom ingredient, a panel appears letting you assign a category and aliases, then save it to Firebase in one click — it immediately appears in autocomplete</li>
            <li><strong>New ingredient not in list?</strong> Use Manage Ingredients to add it with a category and aliases — it will immediately appear in autocomplete for new recipes</li>
            <li><strong>Sharing recipes:</strong> Use Generate JSON to download a recipe as a file, then send it to another user — they can load it instantly with the Import JSON button, and it will be assigned a new ID automatically</li>
            <li><strong>Load recipe search:</strong> The Load Recipe modal has a search box — type a few letters to jump straight to the recipe you want to edit</li>
            <li><strong>Recipe organization:</strong> Use occasions and dish types to make recipes easier to find later</li>
            <li><strong>Multi-part recipes:</strong> Use sections to clearly separate components like dough, filling, and glaze — scaling applies across all sections automatically</li>
          </ul>
        </section>

        {/* ══════════════════════════════════════════════════════════════════
            DATA STORAGE
        ══════════════════════════════════════════════════════════════════ */}
        <section className="guide-section" id="data-storage">
          <h2>💾 Data Storage</h2>
          <p>
            All your data is stored securely in <strong>Firebase</strong> and tied to your account.
            Your meal plan, recipes, ingredients, store items, and images are available on any device you sign in to —
            no local browser storage required.
          </p>

          <div className="info-box">
            <p>
              <strong>🔒 Your data is private.</strong> Each account can only read and write its own data.
              Signing out on a shared device keeps your information safe.
            </p>
          </div>

          <h3>What's Saved to Your Account</h3>
          <ul>
            <li><strong>Weekly meal plan</strong> — your recipe selections, sides, and serving scales for all seven days, synced in real time</li>
            <li><strong>Recipes</strong> — your full recipe library, including any recipes you've added or edited</li>
            <li><strong>Images</strong> — recipe images, step images, and equipment images are stored in <strong>Firebase Storage</strong> under your account. Download URLs are saved with each recipe so images load instantly from the cloud</li>
            <li><strong>Ingredients</strong> — the ingredient list used for autocomplete and grocery list categorisation</li>
            <li><strong>Store items</strong> — the non-food checklist categories and items</li>
          </ul>

          <div className="highlight-box">
            <p>
              <strong>💡 First sign-in:</strong> When you create a new account or sign in with Google for the first time,
              the full default recipe library, ingredient list, and store items are automatically copied into your account.
              From that point on, all changes you make are saved only to your own account.
            </p>
          </div>

          <h3>Managing Ingredients</h3>
          <p>
            Use the <strong>Manage Ingredients</strong> page in the navigation to add, edit, or delete ingredients.
            Changes save directly to Firebase — no JSON file editing required. The <strong>Export JSON</strong> button
            on that page lets you download a backup copy at any time.
          </p>

          <h3>Managing Store Items</h3>
          <p>
            The non-food checklist is stored in your Firebase account. Changes made by an admin can be pushed to the
            database directly. The non-food item checkboxes in the grocery list are session-only — they reset when
            you close the modal, so you always start fresh on your next shopping trip.
          </p>

          <h3>Backing Up Your Meal Plan</h3>
          <p>
            While your data is safely stored in Firebase, you can still export a local copy of your weekly meal plan
            using the <strong>⬇ Export</strong> button in the Weekly Planner. This downloads a <code>.json</code> file
            you can re-import later using the <strong>⬆ Import</strong> button.
          </p>

          <div className="info-box">
            <p>
              <strong>💡 To add a new ingredient:</strong> Go to <strong>Manage Ingredients</strong> in the navigation,
              fill in the name, category, and aliases, then click <strong>Add Ingredient</strong>. It saves instantly to Firebase.
            </p>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════════════════
            GET COOKING
        ══════════════════════════════════════════════════════════════════ */}
        <section className="guide-section get-cooking">
          <h2>👨‍🍳 Ready to Get Cooking?</h2>
          <p>Now that you know all the features, start exploring recipes and planning your meals!</p>
          <Link to="/" className="home-button">Browse Recipes</Link>
        </section>

        {/* ── Back to Top ── */}
        {showBackToTop && (
          <button
            className="back-to-top"
            onClick={scrollToTop}
            aria-label="Back to Quick Navigation"
            title="Back to Quick Navigation"
          >
            ↑ Quick Nav
          </button>
        )}

      </div>
    </motion.div>
  );
}

export default UserGuide;
